import { useEffect, useId, useState } from 'react'
import { Trans } from '@lingui/react/macro'

import { queryClient } from '@jotta/query'
import { Popover, PopoverContent, PopoverTrigger } from '@jotta/ui/Popover'
import SvgBellSimple from '@jotta/ui/SvgBellSimple'
import { getBrandZIndex } from '@jotta/ui/zIndex'

import { PlainBrandIcon } from '../Icons/BrandIcon/BrandIcon'
import { LoadingOverlay } from '../Layout/LoadingOverlay/LoadingOverlay'

import type { NotificationsApi } from './NotificationsApi'
import { notificationsApi, useNotifications } from './NotificationsApi'
import { NotificationsList } from './NotificationsList'

export const Notifications: React.FC<{ api?: NotificationsApi }> = ({
  api = notificationsApi,
  ...props
}) => {
  const {
    notifications,
    hasUnread,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotifications(api)

  const [open, setOpen] = useState(false)

  const unread = notifications.find(n => n.isNew)

  const nameId = useId()

  useEffect(() => {
    if (!unread) {
      return
    }
    if (open) {
      api.markAsRead(unread)
    } else {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
  }, [api, open, unread])

  return (
    <Popover
      data-onboarding-id="notificationsBell"
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <button
          id="notification-alert-button"
          type="button"
          aria-labelledby={`${nameId}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          className="btn btn-elevated relative"
        >
          <span id={nameId} className="sr-only">
            {hasUnread ? (
              <Trans>Open your notifications, unread notification</Trans>
            ) : (
              <Trans>Open your notifications, no unread notifications</Trans>
            )}
          </span>

          <PlainBrandIcon icon={SvgBellSimple} height={24} aria-hidden="true" />

          {/* Badge */}
          {hasUnread && (
            <div className="absolute -right-1 -top-1 h-4 w-4 rounded-full border-2 border-surface-container-low bg-on-primary-container" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        aria-labelledby="notifications-heading"
        style={{ zIndex: getBrandZIndex('dropdown') }}
        className="max-h-[80vh] w-[16.25rem] min-[600px]:w-[25rem]"
        hasArrow={false}
        align="end"
        sideOffset={16}
        onInteractOutside={e => {
          // keep open while paging to avoid accidental dismiss
          if (isFetchingNextPage) {
            e.preventDefault()
          }
        }}
      >
        <LoadingOverlay id="Notifications" open={isLoading} />
        <NotificationsList
          notifications={notifications}
          onFetchNextPage={fetchNextPage}
          hasNextPage={Boolean(hasNextPage)}
          isFetchingNextPage={isFetchingNextPage}
        />
      </PopoverContent>
    </Popover>
  )
}
